<template>
  <div
    class="RadioButtons-container"
    :class="[parentClass, {
      'is-disabled': disabled,
      'is-error': hasError,
      'is-filled': model !== undefined,
    }]"
  >
    <FieldLabel
      v-if="label"
      :required="isRequired"
      :disabled
      :hideAsterisk
    >
      {{ label }}
    </FieldLabel>

    <RadioGroupRoot
      v-model="model"
      :class="['RadioButtons', { [`RadioButtons-${size}`]: size, 'is-error': hasError, 'is-vertical': vertical }]"
      :disabled="disabled"
      :aria-label="label || ariaLabel"
    >
      <RadioGroupItem
        v-for="(option, index) of options"
        :key="option.value"
        :value="option.value"
        :disabled="option.disabled"
        :tabindex="getTabIndex(option, index)"
        class="RadioButtons-item"
      >
        <span class="RadioButtons-inputWrapper">
          <span class="RadioButtons-circle" aria-hidden="true">
            <RadioGroupIndicator class="dot" />
          </span>
          <span class="RadioButtons-heading">{{ option.label }}</span>
        </span>

        <div v-if="option.description" class="RadioButtons-description">
          {{ option.description }}
        </div>
      </RadioGroupItem>
    </RadioGroupRoot>

    <FieldHelper
      :errorMessage="errorMessage"
      :helperText="helperText"
    />
  </div>
</template>

<script lang="ts" setup>
import { useAttrs } from 'vue';
import { useFieldValidation } from '@cvo/ui/composables';
import { RadioGroupRoot, RadioGroupItem, RadioGroupIndicator } from 'reka-ui';
import FieldLabel from '../FieldLabel/FieldLabel.vue';
import FieldHelper from '../FieldHelper/FieldHelper.vue';
import type { RadioButtonsProps, RadioOption } from './RadioButtons.model';

const props = withDefaults(defineProps<RadioButtonsProps>(), {
  size: 'md',
  hideAsterisk: false,
});

const model = defineModel<string | null | undefined>({
  default: undefined,
});

const { hasError, errorMessage, isRequired } = useFieldValidation(props);

const { class: parentClass } = useAttrs();

function getTabIndex(option: RadioOption, index: number) {
  if (option.disabled) return -1;
  if (model.value === option.value) return 0;
  if (model.value == null && index === 0) return 0;

  return -1;
}

defineOptions({
  inheritAttrs: false,
});
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/layout';
@use '@cvo/ui/styles/utils/text' as text;

@layer cvo-ui {
  .RadioButtons {
    --field-label-color: #{vars.$atoms-input-enabled-label-color};

    @mixin radio-colors($bg, $border, $icon) {
      background-color: $bg;
      border-color: $border;

      .dot {
        background-color: $icon;
      }
    }

    display: flex;
    gap: vars.$dimensions-d-5;
    flex-wrap: wrap;

    &.is-vertical {
      flex-direction: column;
    }

    &-container {
      height: 100%;
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-2;

      // TODO CVO-18267: Replace with generic field label tokens once updated in Figma
      --field-label-color: #{vars.$atoms-input-enabled-label-color};

      &.is-filled {
        --field-label-color: #{vars.$atoms-input-filled-label-color};
      }

      &.is-error {
        --field-label-color: #{vars.$atoms-input-error-label-color};
      }

      &.is-disabled {
        --field-label-color: #{vars.$atoms-input-disabled-label-color};

        .RadioButtons-item {
          cursor: not-allowed;
        }
      }
    }

    &-item {
      cursor: pointer;
      border: none;
      background: transparent;
      padding: 0;
      text-align: start;

      &:focus-visible {
        outline: none;

        .RadioButtons-circle {
          outline: 2px solid vars.$color-brand-primary-900;
          outline-offset: 2px;
        }
      }

      &[data-disabled] {
        .RadioButtons-heading {
          color: vars.$atoms-controls-radio-button-disabled-off-primary-text-color;
        }
        .RadioButtons-description {
          color: vars.$atoms-controls-radio-button-disabled-off-secondary-text-color;
        }
      }

      &[data-state='checked'] {
        .RadioButtons-heading {
          color: vars.$atoms-controls-radio-button-default-on-primary-text-color;
        }
        .RadioButtons-description {
          color: vars.$atoms-controls-radio-button-default-on-secondary-text-color;
        }
      }

      &[data-state='checked'][data-disabled] {
        .RadioButtons-heading {
          color: vars.$atoms-controls-radio-button-disabled-on-primary-text-color;
        }
        .RadioButtons-description {
          color: vars.$atoms-controls-radio-button-disabled-on-secondary-text-color;
        }
      }
    }

    &-inputWrapper {
      display: flex;
      gap: vars.$dimensions-d-2;
      align-items: center;
      flex-wrap: nowrap;
      user-select: none;
    }

    &-circle {
      position: relative;

      border-radius: vars.$atoms-controls-radio-button-radio-border-radius;
      border-width: vars.$atoms-controls-radio-button-radio-border-stroke;
      border-style: solid;

      @include radio-colors(
        $bg: vars.$atoms-controls-radio-button-default-off-radio-bg-color,
        $border: vars.$atoms-controls-radio-button-default-off-radio-border-color,
        $icon: vars.$atoms-controls-radio-button-default-off-radio-icon-color
      );

      @include layout.square(20px);
      display: flex;
      align-items: center;
      justify-content: center;

      .dot {
        @include layout.square(10px);
        border-radius: vars.$atoms-controls-radio-button-radio-border-radius;
        display: block;
      }
    }

    // checked style
    &-item[data-state='checked'] &-circle {
      @include radio-colors(
        $bg: vars.$atoms-controls-radio-button-default-on-radio-bg-color,
        $border: vars.$atoms-controls-radio-button-default-on-radio-border-color,
        $icon: vars.$atoms-controls-radio-button-default-on-radio-icon-color
      );
    }

    // disabled style
    &-item[data-disabled] &-circle {
      @include radio-colors(
        $bg: vars.$atoms-controls-radio-button-disabled-off-box-bg-color,
        $border: vars.$atoms-controls-radio-button-disabled-off-box-border-color,
        $icon: vars.$atoms-controls-radio-button-disabled-off-icon-color
      );
    }

    // checked and disabled style
    &-item[data-state='checked'][data-disabled] &-circle {
      @include radio-colors(
        $bg: vars.$atoms-controls-radio-button-disabled-on-radio-bg-color,
        $border: vars.$atoms-controls-radio-button-disabled-on-radio-border-color,
        $icon: vars.$atoms-controls-radio-button-disabled-on-radio-icon-color
      );
    }

    &-heading {
      color: vars.$atoms-controls-radio-button-default-off-primary-text-color;
      @include text.text(md);
      line-height: 100%;
    }

    &-description {
      color: vars.$atoms-controls-radio-button-default-off-secondary-text-color;
      @include text.text(sm);
      line-height: 100%;
      margin-top: vars.$dimensions-d-2;
    }

    &-error {
      display: flex;
      gap: vars.$dimensions-d-1;
      align-items: center;
      color: vars.$atoms-input-error-label-color;
      @include text.text(xs);
      margin-top: vars.$dimensions-d-2;
    }

    // Small size variations
    &-sm {

      .RadioButtons-error,
      .RadioButtons-description {
        margin-top: vars.$dimensions-d-1;
      }

      .RadioButtons-circle {
        @include layout.square(14px); // Smaller circle

        .dot {
          @include layout.square(7px); // Smaller dot
        }
      }

      .RadioButtons-heading {
        @include text.text(xs);
        line-height: 100%;
      }

      .RadioButtons-description {
        @include text.text(xs);
        line-height: 100%;
      }
    }
  }
}
</style>
